import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Button, Row, Col, Spinner, Alert, Table, Badge } from 'react-bootstrap';
import tikaneService from '../services/tikaneService';
import clientService from '../services/clientService';
import transactionTikaneService from '../services/transactionTikaneService';
import ViewPdfButton from './ViewPdfButton';

export default function TiKaneViewModal({ show, onHide, tiKaneId }) {
  const [loading, setLoading] = useState(false);
  const [acc, setAcc] = useState(null);
  const [client, setClient] = useState(null);
  const [txs, setTxs] = useState([]);
  const [summary, setSummary] = useState({});
  const [error, setError] = useState('');

  const fmtMoney = (n) => Number(n || 0).toFixed(2);
  const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : '—');

  useEffect(() => {
    if (!show || !tiKaneId) return;

    setLoading(true);
    setError('');
    setAcc(null);
    setClient(null);
    setTxs([]);
    setSummary({});

    (async () => {
      try {
        const [acct, list] = await Promise.all([
          tikaneService.get(tiKaneId),
          transactionTikaneService.list(tiKaneId, {
            page: 1,
            pageSize: 5,
            sortKey: 'createdAt',
            sortDir: 'desc',
          }),
        ]);

        setAcc(acct);

        if (Array.isArray(list)) {
          const ordered = [...list]
            .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
          setTxs(ordered.slice(0, 5));

          const sumCredit = list.reduce(
            (sum, tx) => sum + (tx.type === 'CREDIT' ? Number(tx.amount || 0) : 0),
            0
          );
          const sumDebit = list.reduce(
            (sum, tx) => sum + (tx.type === 'DEBIT' ? Number(tx.amount || 0) : 0),
            0
          );
          setSummary({
            sumCredit,
            sumDebit,
            currentBalance: sumCredit - sumDebit,
          });
        } else {
          setTxs(Array.isArray(list?.data) ? list.data : []);
          setSummary(list?.summary || {});
        }

        if (acct?.clientId) {
          try {
            const c = await clientService.get(acct.clientId);
            setClient(c);
          } catch {
            setClient(null);
          }
        }
      } catch (e) {
        const msg = e?.response?.data?.message || e?.message || 'Chargement impossible.';
        setError(msg);
      } finally {
        setLoading(false);
      }
    })();
  }, [show, tiKaneId]);

  const sumCredit = Number(summary?.sumCredit || 0);
  const sumDebit = Number(summary?.sumDebit || 0);
  const currentBalance = Number(summary?.currentBalance || 0);

  const remainderToPay = useMemo(() => {
    if (summary?.remainderToPay != null) return Math.max(0, Number(summary.remainderToPay || 0));
    return Math.max(0, Number(acc?.totalValue || 0) - currentBalance);
  }, [acc, currentBalance, summary]);

  const remainingToClient = useMemo(() => {
    if (summary?.remainingToClient != null) return Math.max(0, Number(summary.remainingToClient || 0));
    return Math.max(0, Number(acc?.finalValue || 0) - sumDebit);
  }, [acc, sumDebit, summary]);

  const feePercent = Number(acc?.feePercent ?? 5);
  const feeFixed = Number(acc?.feeFixedGourde ?? 0);
  const percentFeeAmount = Number(acc?.totalValue || 0) * (feePercent / 100);
  const totalFees = percentFeeAmount + feeFixed;
  const isMinor = !!acc?.minorId;

  const getDocument = () => ({
    title: `Fiche compte Ti Kanè ${acc?.accountNumber ? `#${acc.accountNumber}` : ''}`.trim(),
    sections: [
      {
        title: isMinor ? 'Titulaire mineur' : 'Titulaire adulte',
        rows: !isMinor
          ? [
              { label: 'Nom', value: client ? `${client.firstName || ''} ${client.lastName || ''}`.trim() : '—' },
              { label: 'NIF/CIN', value: client?.nifCin || '—' },
              { label: 'Téléphone', value: client?.phone || '—' },
              { label: 'Email', value: client?.email || '—' },
            ]
          : [
              { label: 'Nom mineur', value: `${acc?.Minor?.firstName || ''} ${acc?.Minor?.lastName || ''}`.trim() || '—' },
              { label: 'Parent', value: `${acc?.Minor?.parent?.firstName || ''} ${acc?.Minor?.parent?.lastName || ''}`.trim() || '—' },
              { label: 'NIF/CIN parent', value: acc?.Minor?.parent?.nifCin || '—' },
            ],
      },
      {
        title: 'Compte',
        rows: [
          { label: 'Numéro de compte', value: acc?.accountNumber || '—' },
          { label: 'Statut', value: acc?.status || '—' },
          { label: 'Ouvert le', value: fmtDate(acc?.startDate) },
          { label: 'Durée (jours)', value: acc?.durationDays ?? '—' },
          { label: 'Men', value: fmtMoney(acc?.menCount) },
          { label: 'Valeur / jour', value: fmtMoney(acc?.dailyValue) },
          { label: 'Total à payer', value: fmtMoney(acc?.totalValue) },
          { label: 'Frais (%)', value: `${feePercent.toFixed(2)}%` },
          { label: 'Frais (%) montant', value: fmtMoney(percentFeeAmount) },
          { label: 'Frais fixe', value: fmtMoney(feeFixed) },
          { label: 'Total frais', value: fmtMoney(totalFees) },
          { label: 'Valeur finale', value: fmtMoney(acc?.finalValue) },
          { label: 'Déjà déposé', value: fmtMoney(sumCredit) },
          { label: 'Retiré', value: fmtMoney(sumDebit) },
          { label: 'Solde courant', value: fmtMoney(currentBalance) },
          { label: 'Reste à payer', value: fmtMoney(remainderToPay) },
          { label: 'Dû au client', value: fmtMoney(remainingToClient) },
        ],
      },
      {
        title: 'Dernières transactions',
        rows: txs.length > 0
          ? txs.map((tx, idx) => ({
              label: `Transaction ${idx + 1}`,
              value: `ID: ${tx.id} | Type: ${tx.type} | Montant: ${Number(tx.amount).toFixed(2)} | Solde après: ${Number(tx.balanceAfter).toFixed(2)} | Date: ${new Date(tx.createdAt).toLocaleString()} | Code: ${tx.codeTransaction || '—'}`,
            }))
          : [{ label: 'Transactions', value: 'Aucune transaction.' }],
      },
    ],
  });

  return (
    <Modal show={show} onHide={onHide} size="lg" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>
          Détails du compte {acc?.accountNumber ? `#${acc.accountNumber}` : (tiKaneId ? `#${tiKaneId}` : '')}
          {' '}
          {isMinor ? <Badge bg="warning" text="dark">Mineur</Badge> : <Badge bg="info" text="dark">Adulte</Badge>}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {loading ? (
          <div className="text-center my-5"><Spinner animation="border" /></div>
        ) : error ? (
          <Alert variant="danger">{error}</Alert>
        ) : acc ? (
          <>
            <Row className="mb-3">
              {!isMinor ? (
                <Col md={7}>
                  <h5 className="mb-3">Client</h5>
                  <div><strong>Nom :</strong> {client ? `${client.firstName || ''} ${client.lastName || ''}`.trim() : '—'}</div>
                  <div><strong>NIF/CIN :</strong> {client?.nifCin || '—'}</div>
                  <div><strong>Téléphone :</strong> {client?.phone || '—'}</div>
                  <div><strong>Email :</strong> {client?.email || '—'}</div>
                </Col>
              ) : (
                <Col md={7}>
                  <h5 className="mb-3">Mineur</h5>
                  <div><strong>Nom :</strong> {`${acc.Minor?.firstName || ''} ${acc.Minor?.lastName || ''}`.trim() || '—'}</div>
                  <div className="mt-3">
                    <strong>Parent :</strong>{' '}
                    {`${acc.Minor?.parent?.firstName || ''} ${acc.Minor?.parent?.lastName || ''}`.trim() || '—'}
                    {' '}
                    ({acc.Minor?.parent?.nifCin || '—'})
                  </div>
                </Col>
              )}
              <Col md={5}>
                <h5 className="mb-3">Statut</h5>
                <div>
                  <Badge bg={
                    String(acc.status).toUpperCase() === 'OPEN' ? 'success' :
                    String(acc.status).toUpperCase() === 'CLOSED' ? 'secondary' :
                    'warning'
                  }>
                    {acc.status}
                  </Badge>
                </div>
                <div className="mt-2"><strong>Ouvert le :</strong> {fmtDate(acc.startDate)}</div>
                <div><strong>Durée (jours) :</strong> {acc.durationDays}</div>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={4}>
                <h6 className="mb-2">Montants</h6>
                <div><strong>Men :</strong> {fmtMoney(acc.menCount)}</div>
                <div><strong>Valeur / jour :</strong> {fmtMoney(acc.dailyValue)}</div>
                <div><strong>Total à payer :</strong> {fmtMoney(acc.totalValue)}</div>
                <div><strong>Frais (%) :</strong> {feePercent.toFixed(2)}%</div>
                <div><strong>Frais (%) (montant) :</strong> {fmtMoney(percentFeeAmount)}</div>
                <div><strong>Frais fixe (HTG) :</strong> {fmtMoney(feeFixed)}</div>
                <div><strong>Total des frais :</strong> {fmtMoney(totalFees)}</div>
                <div><strong>Valeur finale :</strong> {fmtMoney(acc.finalValue)}</div>
              </Col>
              <Col md={4}>
                <h6 className="mb-2">Dépôts / Retraits</h6>
                <div><strong>Déjà déposé :</strong> {fmtMoney(sumCredit)}</div>
                <div><strong>Retiré :</strong> {fmtMoney(sumDebit)}</div>
                <div><strong>Solde courant :</strong> {fmtMoney(currentBalance)}</div>
              </Col>
              <Col md={4}>
                <h6 className="mb-2">Restants</h6>
                <Alert variant="info" className="py-2 mb-2">
                  Reste à payer : <strong>{fmtMoney(remainderToPay)}</strong>
                </Alert>
                <Alert variant="success" className="py-2 mb-0">
                  Dû au client : <strong>{fmtMoney(remainingToClient)}</strong>
                </Alert>
              </Col>
            </Row>

            <h5 className="mb-2">Dernières transactions</h5>
            <Table hover responsive size="sm" className="mb-0">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Type</th>
                  <th>Montant</th>
                  <th>Solde après</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {txs.map((tx) => (
                  <tr key={tx.id}>
                    <td>{tx.id}</td>
                    <td>{tx.type}</td>
                    <td>{Number(tx.amount).toFixed(2)}</td>
                    <td>{Number(tx.balanceAfter).toFixed(2)}</td>
                    <td>{new Date(tx.createdAt).toLocaleString()}</td>
                  </tr>
                ))}
                {txs.length === 0 && (
                  <tr><td colSpan="5" className="text-center text-muted">Aucune transaction.</td></tr>
                )}
              </tbody>
            </Table>
          </>
        ) : (
          <Alert variant="warning">Aucune donnée.</Alert>
        )}
      </Modal.Body>

      <Modal.Footer>
        {acc && <ViewPdfButton getDocument={getDocument} className="me-2" />}
        <Button variant="secondary" onClick={onHide}>Fermer</Button>
      </Modal.Footer>
    </Modal>
  );
}
